import React, { useCallback, useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import classnames from 'classnames'
import { useBlocker, useLocation } from 'react-router'
import { LINK_MASS_MESSAGING_BEST_PRACTICES_GUIDELINES } from 'shared/constants/links'
import { uiStore } from 'shared/store/uiStore'
import { Box, FieldDropdown, SpinnerLoader, Tabs, Typography } from 'shared/ui'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { RcsEmptyFallbackInboxAlert, RcsNotSupportedFilesAlert, SendFromRcs } from 'entities/Rcs/ui'
import { AdvancedTrackConversions } from 'widgets/AdvancedTrackConversions/AdvancedTrackConversions'
import { RinglessMessageField } from 'widgets/MessageFormFields/RinglessMessageField'
import { GuidelineAlert, GuidelineMemoryStore } from 'widgets/GuidelineAlert'
import { SendFrom } from 'widgets/SendFrom'
import { ConvertToCampaign } from 'pages/broadcasts/ui/BroadcastView/ui/ConvertToCampaign/ConvertToCampaign'
import { BroadcastAssigneeField } from 'pages/broadcasts/ui/BroadcastView/ui/BroadcastAssignee/ui/BroadcastAssigneeField/BroadcastAssigneeField'
import { BroadcastName } from 'pages/broadcasts/ui/BroadcastView/ui/BroadastName/BroadcastName'
import { BroadcastSMS } from 'pages/broadcasts/ui/BroadcastView/ui/BroadcastSMS/BroadcastSMS'
import { ScheduleRecurringAlert } from 'pages/broadcasts/ui/BroadcastView/ui/Alerts/ScheduleRecurringAlert/ScheduleRecurringAlert'
import { DailyLimitAlert } from 'pages/broadcasts/ui/BroadcastView/ui/Alerts/DailyLimitAlert/DailyLimitAlert'
import { BroadcastBadNumbers } from 'pages/broadcasts/ui/BroadcastView/ui/BroadcastBadNumbers'
import { BroadcastSchedule } from 'pages/broadcasts/ui/BroadcastView/ui/BrooadcastSchedule'
import { BroadcastActions } from 'pages/broadcasts/ui/BroadcastView/ui/BroadcastActions/BroadcastActions'
import { ContactsContent } from 'pages/broadcasts/ui/BroadcastView/ui/ContactsContent'
import { QuietHoursContent } from 'pages/broadcasts/ui/BroadcastView/ui/QuietHoursContent/QuietHoursContent'
import styles from './styles.module.scss'
import { useBroadcastViewContext } from '../context/broadcastViewContext'
import { BroadcastViewPreview } from './BroadcastViewPreview/BroadcastViewPreview'

export const BroadcastViewContent = observer(() => {
  const { state } = useLocation()
  const { subscriptionStore } = useEntitiesContext()
  const { isPAYGPlan } = subscriptionStore
  const store = useBroadcastViewContext()
  const blocker = useBlocker(store.isExistChanges)
  const {
    trackConversionStore,
    isOpenAdvanced,
    setOpenAdvanced,
    showAiReplies,
    aiRepliesStore,
    broadcastSMSStore: {
      messageFieldStore: { showRcsNotSupportedFilesAlert, setShowRcsNotSupportedFilesAlert },
    },
  } = store
  const beforeUnloadHandler = useCallback((event: BeforeUnloadEvent) => {
    event.preventDefault()
    event.returnValue = true
  }, [])

  const guidelineStore = useMemo(() => new GuidelineMemoryStore(), [])

  useEffect(() => {
    if (state?.filters_list_groups) {
      store.broadcastContactsStore.setInitStoredFiltersListGroups(
        JSON.parse(state?.filters_list_groups)
      )
    }

    if (state?.contact_ids && !state?.filters_list_groups) {
      store.broadcastContactsStore.setInitContactIds(state?.contact_ids)
    }
  }, [state])

  useEffect(() => {
    store.broadcastContactsStore.loadFilters()

    return () => {
      store.reset()
      window.removeEventListener('beforeunload', beforeUnloadHandler)
    }
  }, [])

  useEffect(() => {
    uiStore.setRoutesBlocker(blocker)
    store.handleShowUnsavedModal(blocker)
  }, [blocker])

  useEffect(() => {
    uiStore.setRoutesBlocker(store.isExistChanges ? blocker : null)

    if (store.isExistChanges) {
      window.addEventListener('beforeunload', beforeUnloadHandler)
    } else {
      window.removeEventListener('beforeunload', beforeUnloadHandler)
    }
  }, [store.isExistChanges])

  if (store.loading) {
    return (
      <div className={styles.loading}>
        <SpinnerLoader ariaLabel={'BroadcastViewContent'} />
      </div>
    )
  }

  return (
    <div className={styles.wrap}>
      <div
        className={classnames(styles.box, styles.box__main, {
          [styles.box__isRelative]: store.isDraggingCondition,
        })}
      >
        <Typography variant={'body-xs-regular'} className={styles.label} ariaLabel={'NewBroadcast'}>
          {`${store.mode === 'edit' ? 'Edit' : 'New'} broadcast`}
        </Typography>
        {store.mode === 'create' && (
          <GuidelineAlert
            className={styles.guidelineAlert}
            link={LINK_MASS_MESSAGING_BEST_PRACTICES_GUIDELINES}
            message='Mass messaging best practices'
            store={guidelineStore}
          />
        )}
        <BroadcastName />
        <div className={styles.tabs}>
          <Tabs
            variant={'simple'}
            tabs={store.tabs}
            activeTabKey={store.type}
            handleSelectTab={store.selectTypeTab}
          />
        </div>
        {showRcsNotSupportedFilesAlert && store.type === 'rcs' && (
          <RcsNotSupportedFilesAlert
            alertProps={{ marginTop: 8, marginBottom: 4 }}
            onClose={() => setShowRcsNotSupportedFilesAlert(false)}
          />
        )}
        {(store.type === 'sms' || store.type === 'rcs') && <BroadcastSMS />}
        {store.type === 'ringless' && (
          <RinglessMessageField store={store.ringlessMessageFieldStore} dropdownWidth={465} />
        )}
        {store.type === 'rcs' && <RcsEmptyFallbackInboxAlert alertProps={{ marginTop: 32 }} />}
        <ScheduleRecurringAlert />
        <DailyLimitAlert />
        <ConvertToCampaign />
        <div className={styles.section}>
          <div className={styles.section__box}>
            {(store.type === 'sms' || store.type === 'ringless') && (
              <FieldDropdown
                labelText={'Send from'}
                dropdownComponent={
                  <SendFrom
                    triggerVariant={'textField'}
                    store={store.sendFromStore}
                    textFiledTriggerProps={{
                      error: { message: store.errorsMap.get('sendFrom') },
                    }}
                    onClearError={() => store.setError('sendFrom', '')}
                  />
                }
              />
            )}
            {store.type === 'rcs' && store.rcsInbox && <SendFromRcs inbox={store.rcsInbox} />}
          </div>

          <ContactsContent />
          <BroadcastBadNumbers store={store.badNumberStore} />
          <Box height={24} />
          <BroadcastAssigneeField />
          <Box height={24} />
          <div className={styles.section__box}>
            <BroadcastSchedule />
            <QuietHoursContent />
          </div>

          {!isPAYGPlan && (
            <>
              <Box height={26} />
              <AdvancedTrackConversions
                trackConversionStore={trackConversionStore}
                isTrackConversionVisible={store.type === 'sms' || store.type === 'rcs'}
                isOpenAdvanced={isOpenAdvanced}
                setOpenAdvanced={setOpenAdvanced}
                isNew={showAiReplies}
                aiRepliesStore={aiRepliesStore}
              />
            </>
          )}

          <BroadcastActions />
        </div>
      </div>

      <div className={classnames(styles.box, styles.box__preview)}>
        <BroadcastViewPreview />
      </div>
    </div>
  )
})
